"use client";

import { useState } from "react";
import { Notice, PillGroup } from "@/components/tool-kit/primitives";
import {
  FieldPanel,
  ForcePanel,
} from "@/components/tools/physics-fields/gravity-panels";
import {
  type SigChoice,
  SigFigsField,
} from "@/components/tools/physics-fields/shared";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";

/** English strings. A translated content file overrides these through `ui`. */
export const GRAVITATIONAL_FORCE_CALCULATOR_STRINGS = {
  modeLabel: "What do you want to find?",
  modeForce: "Force between two masses",
  modeField: "Gravitational field strength (g)",
  scenarioLabel: "Start from an example",
  custom: "Custom values",
  scenarioNames: {
    "earth-moon": "Earth and Moon (mean distance)",
    "sun-earth": "Sun and Earth",
    "person-earth": "A 70 kg person at Earth's surface",
    "two-people": "Two 70 kg people 1 m apart",
    "one-kilogram": "Two 1 kg masses 1 m apart",
  },
  bodyLabel: "Start from a body",
  bodyNames: {
    sun: "Sun",
    mercury: "Mercury",
    venus: "Venus",
    earth: "Earth",
    moon: "Moon",
    mars: "Mars",
    jupiter: "Jupiter",
    saturn: "Saturn",
    uranus: "Uranus",
    neptune: "Neptune",
    pluto: "Pluto",
  },
  solveFor: "Solve for",
  unitFor: "Unit for {name}",
  mass1: "Mass m₁",
  mass2: "Mass m₂",
  distance: "Distance r between centres",
  distanceHint:
    "Measured centre to centre. For a person standing on Earth, use Earth's radius.",
  force: "Gravitational force F",
  bodyMass: "Mass of the body M",
  bodyRadius: "Radius of the body R",
  altitude: "Altitude h above the surface",
  altitudeHint: "Use 0 for the surface itself.",
  fieldStrength: "Field strength g",
  sigFigs: "Significant figures",
  sigFigsMessage:
    "one: {count} significant figure|other: {count} significant figures",
  results: "Results",
  forceBetween: "Gravitational force",
  accelerationOf1: "Acceleration of m₁",
  accelerationOf2: "Acceleration of m₂",
  inStandardGravity: "In units of standard gravity",
  shareOfSurface: "Share of surface gravity",
  working: "The working",
  forceCaption:
    "Each mass pulls on the other with the same force F, in opposite directions.",
  forceDiagramLabel: "Two masses attracting each other",
  forceDiagramDescription:
    "Two circles labelled m1 and m2, a distance r apart, each with an arrow labelled F pointing toward the other.",
  fieldCaption:
    "The field strength g points toward the centre of the body and depends only on M and the distance R + h.",
  fieldDiagramLabel: "Field strength above a planet",
  fieldDiagramDescription:
    "A large circle labelled M with radius R and a point at height h above its surface, with an arrow labelled g pointing back toward the centre.",
  errorMissing:
    "Fill in every box except the one you are solving for. Scientific notation such as 5.97e24 works.",
  errorPositive:
    "Masses, distances and forces must be greater than zero, and the altitude cannot be negative.",
  errorRange:
    "Those numbers are too large or too small to calculate. Check the units.",
  errorBelowSurface:
    "That field strength is larger than the body's surface value, so the point would lie below the surface. Choose a smaller g.",
  assumptions:
    "Assumes point masses or uniform spheres, whose pull acts as if all their mass sat at the centre (Newton's shell theorem). Rotation, tides and the Moon's or Sun's pull on a surface point are ignored.",
} as const;

type Mode = "force" | "field";

export default function GravitationalForceCalculatorWidget() {
  const strings = useToolUi(GRAVITATIONAL_FORCE_CALCULATOR_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [mode, setMode] = useState<Mode>("force");
  const [sig, setSig] = useState<SigChoice>("4");

  return (
    <>
      <PillGroup
        label={strings.modeLabel}
        name="gf-mode"
        onChange={setMode}
        options={[
          { value: "force", label: strings.modeForce },
          { value: "field", label: strings.modeField },
        ]}
        value={mode}
      />
      {mode === "force" ? (
        <ForcePanel sig={Number(sig)} strings={strings} />
      ) : (
        <FieldPanel sig={Number(sig)} strings={strings} />
      )}
      <SigFigsField
        format={fmt}
        id="gf-sig"
        label={strings.sigFigs}
        locale={locale}
        message={strings.sigFigsMessage}
        onChange={setSig}
        value={sig}
      />
      <Notice>{strings.assumptions}</Notice>
    </>
  );
}
